<template>
  <div class="bottom-nav-preview-wrapper">
    <div class="preview-label mb-2">
      <small class="text-muted">Live Preview</small>
    </div>
    <div
      class="bottom-nav-preview-container"
      :class="{
        'preview-mobile': !isDesktopView,
        'preview-desktop': isDesktopView,
        [`preview-desktop-${desktopPosition}`]: isDesktopView && desktopPosition,
        'hide-icons-mobile': hideIconsOnMobile,
        'hide-labels-mobile': hideLabelsOnMobile,
      }"
    >
      <nav
        class="bottomNavigationBar-preview"
        :style="navStyles"
      >
        <ul>
          <li
            v-for="(item, index) in visibleItems"
            :key="`preview-${index}`"
            :class="{ active: index === activeIndex }"
            :style="getItemStyles(item, index === activeIndex)"
            @click="activeIndex = index"
          >
            <div class="bottomNavigationBar-item">
              <div
                v-if="item.iconSvg"
                class="bottomNavigationBar-icon"
                v-html="getSvgWithColor(item, index === activeIndex)"
              ></div>
              <img v-else-if="item.iconImage" :src="item.iconImage" :alt="item.name" />
              <i v-else-if="item.icon" :class="`fa ${item.icon}`"></i>
              <span>{{ item.name || 'Item' }}</span>
            </div>
          </li>

          <!-- More button if needed -->
          <li
            v-if="needsMoreButton"
            class="bottomNavigationBar-more"
            :class="{ 'dropdown-open': showMoreDropdown }"
          >
            <div
              class="bottomNavigationBar-item bottomNavigationBar-more-btn"
              :style="moreButtonStyles"
              @click="toggleMoreDropdown"
            >
              <i class="fa fa-bars"></i>
              <span>{{ moreButtonLabel }}</span>
            </div>
            <div class="bottomNavigationBar-dropdown">
              <ul>
                <li
                  v-for="(item, index) in hiddenItems"
                  :key="`more-${index}`"
                  :style="getItemStyles(item, false)"
                >
                  <div class="bottomNavigationBar-item">
                    <div
                      v-if="item.iconSvg"
                      class="bottomNavigationBar-icon"
                      v-html="getSvgWithColor(item, false)"
                    ></div>
                    <img v-else-if="item.iconImage" :src="item.iconImage" :alt="item.name" />
                    <i v-else-if="item.icon" :class="`fa ${item.icon}`"></i>
                    <span>{{ item.name || 'Item' }}</span>
                  </div>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </nav>
    </div>
    <div class="preview-controls mt-2">
      <b-form-checkbox
        v-model="isDesktopView"
        size="sm"
        class="mr-3"
      >
        Desktop View
      </b-form-checkbox>
      <small class="text-muted">Click items to see active state</small>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BottomNavPreview',
  props: {
    valueFromDb: {
      type: Object,
      default: null,
    },
    handle: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isDesktopView: false,
      activeIndex: 0,
      showMoreDropdown: false,
    };
  },
  computed: {
    settings() {
      // Get settings from parent component via valueFromDb or props
      // The parent should pass the entire bottomNavigationBar settings object
      return this.valueFromDb || {};
    },
    navData() {
      return this.settings.data || [];
    },
    backgroundColor() {
      return this.settings.backgroundColor || '#ffffff';
    },
    activeBackgroundColor() {
      return this.settings.activeBackgroundColor || null;
    },
    iconColor() {
      return this.settings.iconColor || '#777777';
    },
    activeIconColor() {
      return this.settings.activeIconColor || null;
    },
    showMoreButton() {
      return this.settings.showMoreButton || false;
    },
    showMoreButtonNumber() {
      return parseInt(this.settings.showMoreButtonNumber, 10) || 4;
    },
    showMoreButtonNumberDesktop() {
      return parseInt(this.settings.showMoreButtonNumberDesktop, 10) || parseInt(this.settings.showMoreButtonNumber, 10) || 6;
    },
    showMoreButtonLabel() {
      return this.settings.showMoreButtonLabel || 'More';
    },
    hideIconsOnMobile() {
      return this.settings.hideIconsOnMobile || false;
    },
    hideLabelsOnMobile() {
      return this.settings.hideLabelsOnMobile || false;
    },
    desktopPosition() {
      return this.settings.desktopPosition || 'right';
    },
    styleVariant() {
      return this.settings.styleVariant || 'A';
    },
    activeItems() {
      return this.navData.filter((item) => item.isActive && item.name);
    },
    showMoreLimit() {
      return this.isDesktopView ? this.showMoreButtonNumberDesktop : this.showMoreButtonNumber;
    },
    needsMoreButton() {
      return this.showMoreButton && this.activeItems.length > this.showMoreLimit;
    },
    visibleItems() {
      if (this.needsMoreButton) {
        return this.activeItems.slice(0, this.showMoreLimit - 1);
      }
      return this.activeItems;
    },
    hiddenItems() {
      if (this.needsMoreButton) {
        return this.activeItems.slice(this.showMoreLimit - 1);
      }
      return [];
    },
    navStyles() {
      return {
        backgroundColor: this.backgroundColor,
      };
    },
    moreButtonStyles() {
      return {
        color: this.iconColor,
      };
    },
  },
  methods: {
    getItemStyles(item, isActive) {
      const styles = {};

      if (isActive) {
        if (this.activeBackgroundColor) {
          styles.backgroundColor = this.activeBackgroundColor;
        }
        if (this.activeIconColor) {
          styles.color = this.activeIconColor;
        }
      } else {
        styles.color = this.iconColor;
      }

      return styles;
    },
    getSvgWithColor(item, isActive) {
      if (!item.iconSvg) return '';

      const color = isActive && this.activeIconColor ? this.activeIconColor : this.iconColor;
      const svg = item.iconSvg;

      // Create a temporary DOM element to parse and modify SVG
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = svg;
      const svgElement = tempDiv.querySelector('svg');

      if (svgElement) {
        if (item.svgColorWithStroke) {
          svgElement.setAttribute('stroke', color);
          const paths = svgElement.querySelectorAll('path, circle, rect, line, polyline, polygon');
          paths.forEach((path) => {
            path.setAttribute('stroke', color);
          });
        }
        if (item.svgColorWithFill) {
          const paths = svgElement.querySelectorAll('path, circle, rect, line, polyline, polygon');
          paths.forEach((path) => {
            if (path.getAttribute('fill') !== 'none') {
              path.setAttribute('fill', color);
            }
          });
        }
        return tempDiv.innerHTML;
      }

      return svg;
    },
    toggleMoreDropdown() {
      this.showMoreDropdown = !this.showMoreDropdown;
    },
    handleClickOutside(event) {
      if (this.showMoreDropdown && !this.$el.contains(event.target)) {
        this.showMoreDropdown = false;
      }
    },
  },
  watch: {
    navData: {
      handler() {
        // Reset active index if current item is no longer visible
        if (this.activeIndex >= this.visibleItems.length) {
          this.activeIndex = 0;
        }
      },
      deep: true,
    },
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style lang="scss" scoped>
.bottom-nav-preview-wrapper {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 20px 0;
}

.preview-label {
  font-weight: 600;
  color: #495057;
}

.bottom-nav-preview-container {
  position: relative;
  min-height: 80px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}

.preview-mobile {
  padding-bottom: 60px;
}

.preview-desktop {
  padding: 20px;
  min-height: 200px;
}

.preview-desktop-right,
.preview-desktop-left {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
}

.preview-desktop-bottom {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  padding-bottom: 60px;
}

.bottomNavigationBar-preview {
  position: relative;
  background-color: #ffffff;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
}

.preview-mobile .bottomNavigationBar-preview {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
}

.preview-desktop .bottomNavigationBar-preview {
  position: relative;
  width: auto;
}

.preview-desktop-right .bottomNavigationBar-preview {
  float: right;
}

.preview-desktop-left .bottomNavigationBar-preview {
  float: left;
}

.preview-desktop-bottom .bottomNavigationBar-preview {
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
}

.bottomNavigationBar-preview ul {
  display: flex;
  justify-content: space-around;
  padding: 0;
  margin: 0;
  list-style: none;
}

.preview-desktop .bottomNavigationBar-preview ul {
  flex-direction: column;
  padding: 10px 0;
}

.preview-desktop-bottom .bottomNavigationBar-preview ul {
  flex-direction: row;
}

.bottomNavigationBar-preview li {
  flex: 1;
  text-align: center;
  width: 120px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.preview-desktop .bottomNavigationBar-preview li {
  width: 75px;
}

.bottomNavigationBar-preview li.active {
  // Active state styling handled inline
}

.bottomNavigationBar-preview .bottomNavigationBar-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 4px;
  text-decoration: none;
  transition: color 0.3s ease;
  cursor: pointer;
}

.preview-desktop .bottomNavigationBar-preview .bottomNavigationBar-item {
  padding: 15px 4px;
}

.bottomNavigationBar-preview .bottomNavigationBar-icon {
  max-height: 24px;
  margin-bottom: 5px;
  width: 24px;
}

.bottomNavigationBar-preview .bottomNavigationBar-icon svg {
  width: 100%;
  max-height: 25px;
}

.bottomNavigationBar-preview img {
  max-height: 24px;
  margin-bottom: 5px;
  width: 24px;
}

.bottomNavigationBar-preview i {
  font-size: 24px;
  margin-bottom: 5px;
}

.bottomNavigationBar-preview span {
  font-size: 12px;
  line-height: 1;
}

.preview-mobile.hide-icons-mobile .bottomNavigationBar-preview i,
.preview-mobile.hide-icons-mobile .bottomNavigationBar-preview img,
.preview-mobile.hide-icons-mobile .bottomNavigationBar-preview .bottomNavigationBar-icon {
  display: none;
}

.preview-mobile.hide-labels-mobile .bottomNavigationBar-preview span {
  display: none;
}

.bottomNavigationBar-more {
  position: relative;
}

.bottomNavigationBar-dropdown {
  display: none;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  min-width: 150px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
}

.preview-desktop-right .bottomNavigationBar-dropdown,
.preview-desktop-left .bottomNavigationBar-dropdown {
  bottom: auto;
  top: 50%;
  transform: translateY(-50%);
}

.preview-desktop-right .bottomNavigationBar-dropdown {
  left: auto;
  right: 100%;
  margin-right: 8px;
}

.preview-desktop-left .bottomNavigationBar-dropdown {
  left: 100%;
  right: auto;
  margin-left: 8px;
}

.preview-desktop-bottom .bottomNavigationBar-dropdown {
  bottom: 100%;
  top: auto;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
}

.bottomNavigationBar-more.dropdown-open .bottomNavigationBar-dropdown {
  display: block;
}

.bottomNavigationBar-dropdown ul {
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
}

.bottomNavigationBar-dropdown li {
  flex: none;
  width: 100%;
}

.bottomNavigationBar-dropdown .bottomNavigationBar-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.bottomNavigationBar-dropdown .bottomNavigationBar-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.bottomNavigationBar-dropdown i,
.bottomNavigationBar-dropdown .bottomNavigationBar-icon {
  font-size: 18px;
  margin-bottom: 0;
  flex-shrink: 0;
}

.bottomNavigationBar-dropdown .bottomNavigationBar-icon {
  max-height: 18px;
  width: 18px;
}

.bottomNavigationBar-dropdown img {
  max-height: 18px;
  margin-bottom: 0;
}

.bottomNavigationBar-dropdown span {
  font-size: 14px;
  white-space: nowrap;
}

.preview-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
